import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  HeadingLevel,
} from "docx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

export const exportLawsuitToWord = (dataLoadDB2, dataLawsuit) => {
  const formatDate = (date) =>
    date ? dayjs(date).add(543, "year").format("D MMM YYYY") : "-";

  const formatMoney = (amount) =>
    amount || amount === 0
      ? Number(amount).toLocaleString("th-TH", { minimumFractionDigits: 2 })
      : "-";

  const createHeader = (text) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true, size: 28, underline: {} })],
      spacing: { before: 400, after: 200 },
    });

  const createLabelValue = (label, value) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, bold: true, size: 24 }),
        new TextRun({ text: `${value || "-"}`, size: 24 }),
      ],
      spacing: { after: 100 },
    });

  const formatAddress = (addr) => {
    if (!addr) return "-";
    return [
      addr.ADDR1,
      addr.ADDR2,
      addr.MOOBAN,
      addr.SOI,
      addr.TUMB ? `ต.${addr.TUMB}` : "",
      addr.AUMPDES ? `อ.${addr.AUMPDES}` : "",
      addr.PROVDES ? `จ.${addr.PROVDES}` : "",
      addr.ZIP,
    ]
      .filter(Boolean)
      .join(" ");
  };

  const sections = [];

  // --- 1. ข้อมูลผู้เช่าซื้อ (CUSTOMER) ---
  if (dataLoadDB2?.CUSTOMER) {
    const cus = dataLoadDB2.CUSTOMER;
    sections.push(
      createHeader("1. ข้อมูลผู้เช่าซื้อ"),
      createLabelValue("ชื่อ-นามสกุล", `${cus.SNAM}${cus.NAME1} ${cus.NAME2}`),
      createLabelValue("เลขบัตรประชาชน", cus.CUSCOD),
      createLabelValue("วันเกิด", formatDate(cus.BIRTHDT)),
      createLabelValue("สัญชาติ", cus.NATION),
      createLabelValue("อาชีพ", cus.OCCUP),
      createLabelValue("สถานที่ทำงาน", cus.OFFIC),

      new Paragraph({
        text: "ที่อยู่:",
        bold: true,
        spacing: { before: 100, after: 100 },
      }),

      // ใช้ flatMap เพื่อแยกที่อยู่และเบอร์โทรเป็นคนละ Paragraph (คนละบรรทัด)
      ...(cus.ADDRESS?.flatMap((addr) => [
        new Paragraph({
          text: `• ${addr.ADDRNO === "1" ? "ที่อยู่ทะเบียนบ้าน: " : "ที่อยู่อื่น: "}${formatAddress(addr)}`,
          indent: { left: 400 },
          spacing: { before: 50 },
        }),
        new Paragraph({
          text: `  เบอร์โทร: ${addr.TELP || "-"}`,
          indent: { left: 400 }, // เยื้องเข้าไปเพื่อให้รู้ว่าเป็นข้อมูลย่อยของที่อยู่ด้านบน
          spacing: { after: 100 },
        }),
      ]) || []),
    );
  }

  // --- 2. ข้อมูลผู้ค้ำประกัน (GUARANTORS) ---
  if (dataLoadDB2?.GUARANTORS?.length > 0) {
    sections.push(
      createHeader(
        `2. ข้อมูลผู้ค้ำประกัน (${dataLoadDB2.GUARANTORS.length} คน)`,
      ),
    );
    dataLoadDB2.GUARANTORS.forEach((g, i) => {
      sections.push(
        new Paragraph({
          text: `ผู้ค้ำคนที่ ${g.GARNO || i + 1}: ${g.SNAM}${g.NAME1} ${g.NAME2} (${g.RELATN})`,
          bold: true,
          spacing: { before: 200 },
        }),
        createLabelValue("เลขบัตรประชาชน", g.CUSCOD),
        createLabelValue("วันเกิด", formatDate(g.BIRTHDT)),
        createLabelValue("สถานที่ทำงาน", g.OFFIC),
        ...(g.ADDRESS?.flatMap((addr) => [
          new Paragraph({
            text: `• ${addr.ADDRNO === "1" ? "ที่อยู่ทะเบียนบ้าน: " : "ที่อยู่อื่น: "}${formatAddress(addr)}`,
            indent: { left: 400 },
            spacing: { before: 50 },
          }),
          new Paragraph({
            text: `  เบอร์โทร: ${addr.TELP || "-"}`,
            indent: { left: 400 }, // ขยับเข้าไปอีกนิดเพื่อให้ดูเป็นข้อมูลย่อยของที่อยู่
            spacing: { after: 100 },
          }),
        ]) || []),
      );
    });
  }

  // --- 3. ข้อมูลคดี (LAWSUIT) ---
  if (dataLawsuit) {
    const law = dataLawsuit;
    sections.push(
      createHeader("3. ข้อมูลคดี"),
      createLabelValue("ศาล", law.provincialCourt),
      createLabelValue("เลขคดีดำ", law.blackCaseNumber),
      createLabelValue("วันที่ยื่นฟ้อง", formatDate(law.dateOfPlaint)),
      createLabelValue("เรื่อง", law.subject),
      createLabelValue("วันนัดศาล", formatDate(law.considerationDate)),
      createHeader("4. ข้อมูลทุนทรัพย์"),
      createLabelValue("ทุนทรัพย์", `${formatMoney(law.litigationFunds)} บาท`),
      createLabelValue(
        "ค่าขาดประโยชน์",
        `${formatMoney(law.lackOfBenefits)} บาท`,
      ),
      createLabelValue("ค่าติดตาม", `${formatMoney(law.trackingFee)} บาท`),
    );

    // ตารางค่าใช้จ่าย
    // if (law.workExpenseList?.length > 0) {
    //   sections.push(
    //     createHeader("5. รายการค่าใช้จ่าย"),
    //     new Table({
    //       width: { size: 100, type: WidthType.PERCENTAGE },
    //       rows: [
    //         new TableRow({
    //           children: [
    //             new TableCell({
    //               children: [new Paragraph({ text: "รายการ", bold: true })],
    //             }),
    //             new TableCell({
    //               children: [new Paragraph({ text: "ยอดเบิก", bold: true })],
    //             }),
    //             new TableCell({
    //               children: [new Paragraph({ text: "สถานะ", bold: true })],
    //             }),
    //           ],
    //         }),
    //         ...law.workExpenseList.map(
    //           (ex) =>
    //             new TableRow({
    //               children: [
    //                 new TableCell({
    //                   children: [new Paragraph(ex.description)],
    //                 }),
    //                 new TableCell({
    //                   children: [new Paragraph(formatMoney(ex.NETPAY))],
    //                 }),
    //                 new TableCell({
    //                   children: [
    //                     new Paragraph(
    //                       ex.approvalStatus === 1 ? "อนุมัติแล้ว" : "รออนุมัติ",
    //                     ),
    //                   ],
    //                 }),
    //               ],
    //             }),
    //         ),
    //       ],
    //     }),
    //   );
    // }
  }

  // --- 4. ข้อมูลสัญญา (LOAN) ---
  if (dataLoadDB2?.LOAN) {
    const ln = dataLoadDB2.LOAN;
    sections.push(
      createHeader("6. ข้อมูลสัญญา"),
      createLabelValue("เลขสัญญาปัจจุบัน", ln.CONTNO),
      createLabelValue("เลขสัญญาเก่า", ln.ISSUNO),
      createLabelValue("วันที่ทำสัญญา", formatDate(ln.SDATE)),
      createLabelValue("ยอดกู้", `${formatMoney(ln.NCSHPRC)} บาท`),
      createLabelValue("ยอดกู้รวมดอก", `${formatMoney(ln.TOTPRC)} บาท`),
      createLabelValue("ค่างวด/เดือน", `${formatMoney(ln.TOT_UPAY)} บาท`),
      createLabelValue("จำนวนงวด", `${ln.T_NOPAY} งวด`),
      createLabelValue(
        "ค้างชำระ",
        `${ln.EXP_FRM} - ${ln.EXP_TO} (${ln.EXP_PRD} งวด)`,
      ),
      createLabelValue("ยอดค้างชำระ", `${formatMoney(ln.EXP_AMT)} บาท`),
      createLabelValue("ลูกหนี้อื่นๆ", `${formatMoney(ln.PAYAMTAROTHR)} บาท`),
    );
  }

  // --- 5. ข้อมูลรถ (MORTGAGE) ---
  if (dataLoadDB2?.MORTGAGE) {
    const m = dataLoadDB2.MORTGAGE;
    sections.push(
      createHeader("7. ข้อมูลรถ"),
      createLabelValue("ยี่ห้อ/รุ่น/แบบ", `${m.TYPE} ${m.MODEL}/${m.BAAB}`),
      createLabelValue("ทะเบียน", m.REGNO),
      createLabelValue("สี", m.COLOR),
      createLabelValue("เลขตัวถัง", m.STRNO),
      createLabelValue("เลขเครื่อง", m.ENGNO),
      createLabelValue("จดทะเบียน", m.DORECV),
    );
  }

  const doc = new Document({
    sections: [
      {
        children: [
          new Paragraph({
            text: `ข้อมูลสัญญา - ${dataLoadDB2.LOAN.CONTNO}`,
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
          }),
          ...sections,
        ],
      },
    ],
  });

  Packer.toBlob(doc).then((blob) => {
    saveAs(
      blob,
      `ข้อมูลสัญญา_${dataLoadDB2?.LOAN?.CONTNO || "ดาวน์โหลด"}.docx`,
    );
  });
};
